/**
 * Import Modal Component for MDM
 *
 * Purpose: Modal dialog for bulk importing master data values from CSV/Excel files
 * Architecture: React modal with file upload, progress tracking, and error handling
 * Dependencies: React, Material-UI, MDM service
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { LoadingButton } from '../common/LoadingButton';
import { styled } from '@mui/material/styles';
import { mdmService } from '../../services/mdmService';
import { websocketService } from '../../services/websocketService';
import {
  MasterDataImportBatch,
  ImportStatus,
  FileUploadResponse,
  BulkOperationResult,
  WebSocketMessage,
  WebSocketMessageType,
} from '../../types/mdm';
import { SecondaryButton } from '../common/ActionButtons';

// Styled components
const UploadArea = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&.drag-over': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
}));

// Props interface
export interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  fielddef_id: string;
  tenant_id: string;
  onImportComplete?: (result: BulkOperationResult) => void;
}

// Import step enumeration
enum ImportStep {
  FILE_UPLOAD = 0,
  PROCESSING = 1,
  RESULTS = 2,
}

/**
 * Import Modal Component
 *
 * Purpose: Handle bulk import of master data values with progress tracking
 * Features: File upload, validation, progress tracking, error reporting
 */
const ImportModal: React.FC<ImportModalProps> = ({
  open,
  onClose,
  fielddef_id,
  tenant_id,
  onImportComplete,
}) => {
  // State management
  const [activeStep, setActiveStep] = useState<ImportStep>(ImportStep.FILE_UPLOAD);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [_uploadResponse, setUploadResponse] = useState<FileUploadResponse | null>(null);
  const [importBatch, setImportBatch] = useState<MasterDataImportBatch | null>(null);
  const [finalResult, setFinalResult] = useState<BulkOperationResult | null>(null);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  /**
   * Reset modal state when opened/closed
   */
  useEffect(() => {
    if (open) {
      setActiveStep(ImportStep.FILE_UPLOAD);
      setSelectedFile(null);
      setUploading(false);
      setProcessing(false);
      setError(null);
      setUploadResponse(null);
      setImportBatch(null);
      setFinalResult(null);
      setDragOver(false);
      setShowErrors(false);
    }
  }, [open]);

  /**
   * Poll import batch status
   */
  const pollBatchStatus = useCallback(
    async (batchId: string) => {
      try {
        const batch = await mdmService.getImportBatchStatus(batchId);
        setImportBatch(batch);

        if (batch.status === ImportStatus.COMPLETED || batch.status === ImportStatus.FAILED) {
          setProcessing(false);
          setActiveStep(ImportStep.RESULTS);

          // Create final result from batch
          const result: BulkOperationResult = {
            total_requested: batch.total_rows,
            successful: batch.processed_rows - batch.error_rows,
            failed: batch.error_rows,
            errors: batch.error_summary
              ? Object.entries(batch.error_summary)
                  // ORCH-282: overridden_from_global is informational, stored
                  // alongside error data in this JSON column - not itself an error.
                  .filter(([type]) => type !== 'overridden_from_global')
                  .map(([type, message]) => ({
                    message: String(message),
                    type,
                  }))
              : [],
            overridden_from_global: batch.overridden_from_global,
          };
          setFinalResult(result);

          if (batch.status === ImportStatus.COMPLETED) {
            onImportComplete?.(result);
          }
        } else {
          // Continue polling
          setTimeout(() => pollBatchStatus(batchId), 2000);
        }
      } catch (err: any) {
        setError(`Failed to check import status: ${err.message}`);
        setProcessing(false);
      }
    },
    [onImportComplete]
  );

  /**
   * Setup WebSocket for real-time progress updates
   */
  useEffect(() => {
    if (!importBatch?.id || !processing) return;

    const handleImportProgress = (message: WebSocketMessage) => {
      if (
        message.type === WebSocketMessageType.IMPORT_PROGRESS &&
        message.data.batch_id === importBatch.id
      ) {
        setImportBatch((prev) => (prev ? { ...prev, ...message.data } : null));
      }
    };

    websocketService.setCallbacks({
      onImportProgress: handleImportProgress,
    });

    return () => {
      websocketService.setCallbacks({});
    };
  }, [importBatch?.id, processing]);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file: File) => {
    const validation = mdmService.validateImportFile(file);
    if (!validation.valid) {
      setError(validation.message || 'Invalid file format');
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  /**
   * Handle file drop
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  /**
   * Handle file upload
   */
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    try {
      const response = await mdmService.uploadImportFile(fielddef_id, tenant_id, selectedFile);
      setUploadResponse(response);
      setActiveStep(ImportStep.PROCESSING);
      setProcessing(true);

      // Start polling for batch status
      await pollBatchStatus(response.batch_id);
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!uploading && !processing) {
      onClose();
    }
  };
  /**
   * Format file size
   */
  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={uploading || processing}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon />
            <Typography variant="h6">Import Master Data Values</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={uploading || processing}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Import Stepper */}
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: File Upload */}
          <Step>
            <StepLabel>Upload File</StepLabel>
            <StepContent>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select a CSV or Excel file containing the master data values to import.
              </Typography>

              {!selectedFile ? (
                <UploadArea
                  className={dragOver ? 'drag-over' : ''}
                  onClick={() => document.getElementById('file-input')?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                >
                  <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Choose or drag a file here
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supported formats: CSV, Excel (.xlsx, .xls)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Maximum file size: 20MB
                  </Typography>

                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                  />
                </UploadArea>
              ) : (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <FileIcon color="primary" />
                    <Box flex={1}>
                      <Typography variant="subtitle2">{selectedFile.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedFile(null)}
                      disabled={uploading}
                    >
                      Change
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Upload Button */}
              <Box sx={{ mb: 1 }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleUpload}
                  loading={uploading}
                  disabled={!selectedFile}
                  startIcon={<UploadIcon />}
                >
                  Upload and Process
                </LoadingButton>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Processing */}
          <Step>
            <StepLabel>Processing</StepLabel>
            <StepContent>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Processing the uploaded file and importing values...
              </Typography>

              {importBatch && (
                <ProgressContainer>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Progress: {importBatch.processed_rows} / {importBatch.total_rows} rows
                    </Typography>
                    <Typography variant="body2">
                      {importBatch.progress_percentage.toFixed(1)}%
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={importBatch.progress_percentage}
                    sx={{ height: 8, borderRadius: 4 }}
                  />

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Success Rate: {importBatch.success_rate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Errors: {importBatch.error_rows}
                    </Typography>
                  </Box>
                </ProgressContainer>
              )}
            </StepContent>
          </Step>

          {/* Step 3: Results */}
          <Step>
            <StepLabel>Results</StepLabel>
            <StepContent>
              {finalResult && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Import Summary
                    </Typography>

                    <Box display="flex" gap={2} mb={2}>
                      <Chip
                        icon={<SuccessIcon />}
                        label={`${finalResult.successful} Successful`}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        icon={<ErrorIcon />}
                        label={`${finalResult.failed} Failed`}
                        color="error"
                        variant="outlined"
                      />
                      {!!finalResult.overridden_from_global && (
                        <Chip
                          label={`${finalResult.overridden_from_global} Overridden from GLOBAL`}
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {finalResult.errors.length > 0 && (
                      <>
                        <Button
                          startIcon={showErrors ? <CollapseIcon /> : <ExpandIcon />}
                          onClick={() => setShowErrors(!showErrors)}
                          sx={{ mb: 1 }}
                        >
                          {showErrors ? 'Hide' : 'Show'} Error Details ({finalResult.errors.length})
                        </Button>

                        <Collapse in={showErrors}>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Error Type</TableCell>
                                  <TableCell>Message</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {finalResult.errors.map((error, index) => (
                                  <TableRow key={index}>
                                    <TableCell>
                                      <Chip label={error.type} size="small" color="error" />
                                    </TableCell>
                                    <TableCell>{error.message}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </>
                    )}
                  </Box>

                  <Alert severity={finalResult.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    {finalResult.failed > 0
                      ? `Import completed with ${finalResult.failed} errors. Please review the error details above.`
                      : `Import completed successfully! ${finalResult.successful} values were imported.`}
                  </Alert>
                </>
              )}
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={uploading || processing}>
          {activeStep === ImportStep.RESULTS ? 'Close' : 'Cancel'}
        </SecondaryButton>

        {activeStep === ImportStep.RESULTS && finalResult && finalResult.successful > 0 && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              onClose();
              // The parent component should refresh the value list
            }}
          >
            View Updated List
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportModal;
